/**
 * LandingNav — sticky marketing nav for /landing, /features, and the
 * dedicated /features/* feature pages.
 *
 * Structure (left → right):
 *   • wordmark brand → /landing (marketing root, like every web logo)
 *   • Product ▾ dropdown — groups the public product surfaces. Items are
 *     context-aware: in-page anchors when the section lives on the current
 *     page (#how, #strategy on /landing), cross-page links otherwise.
 *     Due diligence + Deal copilot are now their own indexable pages.
 *   • Learn → the Astro content hub at /learn
 *   • The 2026 Budget pill — temporal promo, the SOLE pointer to #budget
 *   • Start free — primary CTA, kept visible even on mobile
 *
 * Mobile (<980px): the link cluster collapses behind a hamburger that
 * opens a slide-in drawer. The CTA stays in the bar so conversion is
 * never buried. (Previously the links were just display:none on mobile —
 * i.e. no menu at all.)
 *
 * Dropdown + drawer are click-to-open, close on outside-click / Escape /
 * link-click, and respect prefers-reduced-motion via CSS.
 */

import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { track } from '../../utils/analytics';

export default function LandingNav() {
  const { pathname } = useLocation();
  const onLanding = pathname === '/' || pathname === '/landing';

  // In-page anchor when the section lives on /landing; cross-page otherwise.
  const landingHref = (anchor) => (onLanding ? `#${anchor}` : `/landing#${anchor}`);

  // Single source of truth for the product surfaces — rendered in both the
  // desktop dropdown and the mobile drawer.
  const PRODUCT = [
    { label: 'How it works',       desc: 'Watch Heyward work through real product screens',      href: '/how-it-works' },
    { label: 'The agent',          desc: 'The line through goal, research, offer, settlement', href: landingHref('how') },
    { label: 'Portfolio strategy', desc: 'Your freedom year, stress-tested',                   href: landingHref('strategy') },
    { label: 'Due diligence',      desc: 'Fifty signals deep, one address at a time',           href: '/features/due-diligence' },
    { label: 'Purchase management', desc: 'Fair value, offer, walk-away · every number traceable', href: '/features/deal-copilot' },
  ];

  const [productOpen, setProductOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const productRef = useRef(null);
  const productBtnRef = useRef(null);

  // Dropdown: close on outside-click + Escape (returns focus to trigger).
  useEffect(() => {
    if (!productOpen) return undefined;
    const onDown = (e) => {
      if (productRef.current && !productRef.current.contains(e.target)) setProductOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') { setProductOpen(false); productBtnRef.current?.focus(); }
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [productOpen]);

  // Drawer: close on Escape; lock body scroll while open.
  useEffect(() => {
    if (!mobileOpen) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setMobileOpen(false); };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [mobileOpen]);

  return (
    <nav className="nav">
      <div className="container nav-inner">
        <a href="/landing" className="brand">
          <span className="brand-name"><span>Heyward</span></span>
        </a>

        <div className="nav-actions">
          {/* Desktop link cluster — hidden < 980px (drawer takes over) */}
          <div className="nav-menu">
            <div className="nav-product" ref={productRef}>
              <button
                type="button"
                ref={productBtnRef}
                className={'nav-link nav-product-btn' + (productOpen ? ' open' : '')}
                aria-haspopup="true"
                aria-expanded={productOpen}
                aria-controls="nav-product-menu"
                onClick={() => setProductOpen((o) => !o)}
              >
                Product
                <span className="nav-caret" aria-hidden="true" />
              </button>
              <div
                id="nav-product-menu"
                className={'nav-product-menu' + (productOpen ? ' open' : '')}
                role="menu"
              >
                {PRODUCT.map((item) => (
                  <a
                    key={item.label}
                    href={item.href}
                    className="nav-product-item"
                    role="menuitem"
                    onClick={() => setProductOpen(false)}
                  >
                    <span className="npi-label">{item.label}</span>
                    <span className="npi-desc">{item.desc}</span>
                  </a>
                ))}
              </div>
            </div>

            <a className="nav-link" href="/learn">Learn</a>
            <a className="nav-link" href="/pricing">Pricing</a>

            <span className="nav-sep" aria-hidden="true" />

            <a className="nav-link nav-budget" href={landingHref('budget')}>
              <span className="nb-grad">The 2026 Budget</span>
              <span className="nb-tail">{' '}impact</span>
              <span className="nb-dot" aria-hidden="true" />
            </a>

            <a className="btn btn-ghost nav-signin" href="/sign-in">Sign in</a>
          </div>

          {/* CTA stays visible at every width */}
          <a className="btn btn-primary nav-cta" href="/sign-up" onClick={() => track.waitlistCtaClicked('nav')}>
            Start free <span className="arr" aria-hidden="true">→</span>
          </a>

          {/* Hamburger — shown < 980px */}
          <button
            type="button"
            className={'nav-burger' + (mobileOpen ? ' open' : '')}
            aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={mobileOpen}
            onClick={() => setMobileOpen((o) => !o)}
          >
            <span /><span /><span />
          </button>
        </div>
      </div>

      {/* Mobile drawer + scrim */}
      <div
        className={'nav-scrim' + (mobileOpen ? ' open' : '')}
        onClick={() => setMobileOpen(false)}
        aria-hidden="true"
      />
      <aside className={'nav-drawer' + (mobileOpen ? ' open' : '')} aria-hidden={!mobileOpen}>
        <div className="nav-drawer-eyebrow">Product</div>
        {PRODUCT.map((item) => (
          <a
            key={item.label}
            href={item.href}
            className="nav-drawer-item"
            tabIndex={mobileOpen ? 0 : -1}
            onClick={() => setMobileOpen(false)}
          >
            <span className="ndi-label">{item.label}</span>
            <span className="ndi-desc">{item.desc}</span>
          </a>
        ))}

        <div className="nav-drawer-rule" />

        <a
          href="/learn"
          className="nav-drawer-link"
          tabIndex={mobileOpen ? 0 : -1}
          onClick={() => setMobileOpen(false)}
        >
          Learn
        </a>
        <a
          href="/pricing"
          className="nav-drawer-link"
          tabIndex={mobileOpen ? 0 : -1}
          onClick={() => setMobileOpen(false)}
        >
          Pricing
        </a>
        <a
          href="/sign-in"
          className="nav-drawer-link"
          tabIndex={mobileOpen ? 0 : -1}
          onClick={() => setMobileOpen(false)}
        >
          Sign in
        </a>
        <a
          href={landingHref('budget')}
          className="nav-drawer-link nav-drawer-budget"
          tabIndex={mobileOpen ? 0 : -1}
          onClick={() => setMobileOpen(false)}
        >
          <span className="nb-grad">The 2026 Budget</span> impact
          <span className="nb-dot" aria-hidden="true" />
        </a>
      </aside>
    </nav>
  );
}
